import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { draftBlockerCount, subscribeToDraftBlockers } from '@/local-sync/draftRegistry';
import {
  clearIncompatibleReload,
  reloadedBecauseIncompatible,
  type IPendingDeployment,
} from '@/hooks/useDeploymentReload';
import { isServerBehind, subscribeToServerBehind } from '@/services/olderServer';
import { banners } from '@featit/shared/i18n/messages/en/banners';
import { lazyQuiet } from '@/routing/lazyQuiet';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT, type TFn } from '@/i18n/LocaleProvider';

registerEnglishArea('banners', banners);

/**
 * "FEATit was updated" — the reader's own way to take a waiting build (I-0781, T-0329).
 *
 * A new build is never loaded under the screen a reader is on: it waits for their next navigation,
 * and this is how a reader who never navigates takes it. An outgrown client (a higher client
 * contract, T-0329) reloads by itself; this is where it says it is waiting for a draft, and where the
 * page it reloaded into says why.
 *
 * ⛔ **It lives in a slot the chrome always reserves, never over the page (QA 267).** It was a card
 * floating in the corner: it moved no content, but at 1440 px it covered the comment composer's Post
 * button, which is exactly the control its own "Save or discard them first" needs, and at 1024 px the
 * lower right of the reading pane. A slot that is always there, empty or not, meets both rules at every
 * width: nothing moves when it fills, and nothing can be under it.
 *
 * - With the sidebar pinned (1024 px and up), a row at the top of the sidebar's footer, above Agents.
 *   The footer is anchored to the bottom and the row's height is held whether or not it shows
 *   anything, so the page list above and everything below keep their places.
 * - Below that, a button-sized slot in the title bar beside the bell. The bar's second line — the
 *   scope line — carries the sentence when there is one to say (a draft in the way, an outgrown page,
 *   a page that reloaded); the plain update is the icon with a dot, its sentence the button's name.
 * - The iOS app shell draws neither bar, so there it shows nothing: the build is still taken at the
 *   next navigation, and an outgrown page still reloads once no draft holds it.
 *
 * A polite live region with no focus call, so a reader typing or reading keeps their caret.
 */

/**
 * `server-behind` (I-1383): the server refused a read for naming a field it does not have yet, so this
 * app is newer than its server. Reload cannot cure that, so it offers Close, and once closed it stays
 * closed for the page.
 */
export type DeploymentUpdateStateType =
  | 'none'
  | 'update'
  | 'incompatible'
  | 'server-behind'
  | 'reloaded';

export interface IDeploymentUpdateView {
  state: DeploymentUpdateStateType;
  /** Reload was pressed while an unsaved draft is dirty; cleared once no draft is. */
  blockedByDrafts: boolean;
  reload: () => void;
  /**
   * Closes the "reloaded" and "server-behind" messages. An update has no "not now": it covers
   * nothing, so it stays.
   */
  dismiss: () => void;
}

const NONE: IDeploymentUpdateView = {
  state: 'none',
  blockedByDrafts: false,
  reload: () => undefined,
  dismiss: () => undefined,
};

const DeploymentUpdateContext = createContext<IDeploymentUpdateView>(NONE);

export function useDeploymentUpdate(): IDeploymentUpdateView {
  return useContext(DeploymentUpdateContext);
}

export function DeploymentUpdateProvider({
  update,
  children,
}: {
  update: IPendingDeployment | null;
  children: ReactNode;
}) {
  const [blockedByDrafts, setBlockedByDrafts] = useState(false);
  // T-0329: this page replaced one the server had outgrown; it says so until closed.
  const [reloadedOutgrown, setReloadedOutgrown] = useState(reloadedBecauseIncompatible);
  useEffect(() => clearIncompatibleReload(), []);
  const serverBehind = useSyncExternalStore(subscribeToServerBehind, isServerBehind);
  const [serverBehindClosed, setServerBehindClosed] = useState(false);

  // Once the draft that was in the way is saved or discarded, the note about it goes.
  useEffect(() => {
    if (!blockedByDrafts) return undefined;
    return subscribeToDraftBlockers(() => {
      if (draftBlockerCount() === 0) setBlockedByDrafts(false);
    });
  }, [blockedByDrafts]);

  const reload = useCallback(() => {
    if (update?.reloadNow() === 'blocked-by-drafts') setBlockedByDrafts(true);
  }, [update]);

  const view = useMemo<IDeploymentUpdateView>(() => {
    if (update?.incompatible) {
      return { state: 'incompatible', blockedByDrafts, reload, dismiss: () => undefined };
    }
    if (update) return { state: 'update', blockedByDrafts, reload, dismiss: () => undefined };
    if (serverBehind && !serverBehindClosed) {
      return { ...NONE, state: 'server-behind', dismiss: () => setServerBehindClosed(true) };
    }
    if (reloadedOutgrown) {
      return { ...NONE, state: 'reloaded', dismiss: () => setReloadedOutgrown(false) };
    }
    return NONE;
  }, [blockedByDrafts, reload, reloadedOutgrown, serverBehind, serverBehindClosed, update]);

  return (
    <DeploymentUpdateContext.Provider value={view}>{children}</DeploymentUpdateContext.Provider>
  );
}

/** The whole sentence, for a screen reader and a tooltip; the slots show a shorter one. */
export function deploymentUpdateSentence(view: IDeploymentUpdateView, t: TFn): string | null {
  if (view.state === 'none') return null;
  if (view.state === 'reloaded') return t('banners.deploymentUpdate.sentence.reloaded');
  if (view.state === 'server-behind') return t('banners.deploymentUpdate.sentence.serverBehind');
  if (view.blockedByDrafts) return t('banners.deploymentUpdate.sentence.blockedByDrafts');
  if (view.state === 'incompatible') return t('banners.deploymentUpdate.sentence.incompatible');
  return t('banners.deploymentUpdate.sentence.update');
}

/** What the title bar's scope line says instead of the scope, or `null` to leave the scope be. */
export function deploymentUpdateScopeLine(view: IDeploymentUpdateView, t: TFn): string | null {
  if (view.state === 'reloaded') return t('banners.deploymentUpdate.scope.reloaded');
  if (view.state === 'server-behind') return t('banners.deploymentUpdate.scope.serverBehind');
  if (view.blockedByDrafts) return t('banners.deploymentUpdate.scope.blockedByDrafts');
  if (view.state === 'incompatible') return t('banners.deploymentUpdate.scope.incompatible');
  return null;
}

/**
 * T-0968: what the slots draw once there is an update, its own chunk (`DeploymentUpdateFilled.tsx`).
 * The slots hold their size, so it arriving later moves nothing.
 */
const RowStatus = lazyQuiet(
  () => import('./DeploymentUpdateFilled'),
  (module) => module.DeploymentUpdateRowStatus,
);
const HeaderButton = lazyQuiet(
  () => import('./DeploymentUpdateFilled'),
  (module) => module.DeploymentUpdateHeaderButton,
);

/** Reads the filled slots' chunk ahead of an update (`Layout`, after the boot's requests). */
export function preloadDeploymentUpdateParts(): void {
  RowStatus.preload();
  HeaderButton.preload();
}

/**
 * The sidebar footer's update row. By default its height is held when empty; the sidebar's footer
 * passes `reserve={false}` (F-0519, IP-45): it is anchored to the bottom, so the row filling moves
 * none of the rows below it, and an empty one is no band above Agents.
 */
export function DeploymentUpdateRow({ reserve = true }: { reserve?: boolean } = {}) {
  const t = useT();
  const view = useDeploymentUpdate();
  const sentence = deploymentUpdateSentence(view, t);
  /*
    F-0415: the phone menu is one scrolling list, with nothing pinned beneath this row to hold
    still, so there the row takes no room until it has something to say (`reserve: false`). The
    sidebar's footer says the same since IP-45: what is pinned beneath it is pinned to the bottom.
  */
  if (!reserve && (view.state === 'none' || !sentence)) return null;
  return (
    <div data-testid="deployment-update-slot" data-state={view.state} className="h-10">
      {view.state !== 'none' && sentence && <RowStatus view={view} sentence={sentence} />}
    </div>
  );
}

/**
 * The title bar's reserved slot, beside the bell. ⛔ Always the same size, so the title never
 * re-flows when it fills. Its sentence goes on the scope line (`deploymentUpdateScopeLine`).
 */
export function DeploymentUpdateHeaderSlot() {
  const t = useT();
  const view = useDeploymentUpdate();
  const sentence = deploymentUpdateSentence(view, t);
  return (
    <div
      data-testid="deployment-update-header-slot"
      data-state={view.state}
      className="h-touch w-touch shrink-0"
    >
      {view.state !== 'none' && sentence && (
        <span role="status" aria-live="polite" className="sr-only">
          {sentence}
        </span>
      )}
      {view.state !== 'none' ? <HeaderButton view={view} sentence={sentence} /> : null}
    </div>
  );
}
